import styled from "@emotion/styled"
import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material"
import * as api from "api"
import { Desktop, Mobile } from "common/MediaQueries"
import Head from "next/head"
import { useState } from "react"
import sanitizeHtml from "sanitize-html"
import colors from "theme/colors"

const ANSWER_SANITIZE_CONFIG: sanitizeHtml.IOptions = {
  allowedTags: ["b", "strong", "i", "em", "u", "a", "ul", "ol", "li", "p", "br"],
  allowedAttributes: { a: ["href", "target", "rel", "title"] },
}

const STRIP_HTML_CONFIG: sanitizeHtml.IOptions = {
  allowedTags: [],
  allowedAttributes: {},
}

const stripHtml = (html: string) => sanitizeHtml(html, STRIP_HTML_CONFIG)

const Section = styled.section<{ $isModelPage?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 32px ${(p) => (p.$isModelPage ? "15px" : "0")};
  width: 100%;

  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 40px;
    padding: 40px 0;
    max-width: 675px;
    margin: 0 auto;
  }
`

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  color: ${colors.black};
  max-width: 600px;
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
    align-items: center;
  }
`

const Title = styled(Typography)`
  && {
    font-size: 24px;
    font-weight: 300;
    line-height: 1.6;
    letter-spacing: 0;
    margin: 0;
    padding: 0;
    border-bottom: none;

    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 32px;
      letter-spacing: 0.96px;
      text-align: center;
    }
  }
`

const Subtitle = styled(Typography)`
  && {
    font-size: 18px;
    font-weight: 300;
    line-height: 1.3;
    max-width: 600px;

    ${(p) => p.theme.breakpoints.up("md")} {
      text-align: center;
      font-size: 24px;
      line-height: 35px;
    }
  }
`

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 675px;
`

const StyledAccordion = styled(Accordion)`
  && {
    background-color: ${colors.white};
    border: none;
    border-radius: 0;
    box-shadow: none;
    margin: 0;
    width: 100%;
    padding: 0;

    &:not(:last-child) {
      border-bottom: 1px solid ${colors.silver};
    }

    &.prev-to-expanded {
      border-bottom: none;
    }

    &.Mui-expanded {
      background-color: ${colors.whiteTwo};
      border-bottom: none;
      margin: 0;
    }

    &:before {
      display: none;
    }
  }
`

const StyledAccordionSummary = styled(AccordionSummary)`
  && {
    background-color: transparent;
    padding: 16px 8px;
    min-height: 0;
    justify-content: flex-start;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 12px;

    .MuiAccordionSummary-content {
      margin: 0;
      padding: 0;
      color: ${colors.black};
      font-size: 18px;
      font-weight: 500;
      border: none;

      &.Mui-expanded {
        margin: 0;
        padding: 0;
      }
    }

    &.Mui-expanded {
      min-height: 0;
      border-bottom: none;
    }

    .MuiIconButton-root {
      padding: 0;
      margin: 0;
      color: ${colors.black};
    }

    .MuiAccordionSummary-expandIconWrapper {
      transform: none;

      &.Mui-expanded {
        transform: none;
      }
    }
  }
`

const QuestionText = styled(Typography)`
  && {
    font-size: 18px;
    font-weight: 300;
    line-height: 1.3;
    color: ${colors.black};
    flex: 1;

    &.is-expanded {
      font-weight: 300;
    }
  }
`

const StyledAccordionDetails = styled(AccordionDetails)`
  && {
    padding: 0 8px 24px 44px;
  }
`

const AnswerText = styled(Typography)<{ component: string }>`
  && {
    width: 100%;
    unicode-bidi: plaintext;
    color: ${colors.black};
    font-size: 16px;
    font-weight: 300;
    line-height: 24px;

    p {
      margin: 0 0 8px;
    }
    p:last-child {
      margin-bottom: 0;
    }
    ul,
    ol {
      margin: 0 0 8px;
      padding-right: 20px;
    }
    a {
      color: inherit;
      text-decoration: underline;
    }
  }
`

type Props = {
  data: api.FAQ
  isModelPage?: boolean
}

const FAQComp = ({ data, isModelPage }: Props) => {
  const [expanded, setExpanded] = useState<number | false>(false)

  if (!data?.faqLists?.length) return null

  const handleChange =
    (panel: number) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqLists.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: stripHtml(item.answer),
      },
    })),
  }

  const jsonLdSafe = JSON.stringify(faqJsonLd).replace(/</g, "\\u003c")

  return (
    <Section id="faq" $isModelPage={isModelPage}>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdSafe }}
        />
      </Head>
      <Heading>
        <Mobile>
          <Title variant="h3">{data.title}</Title>
        </Mobile>
        <Desktop>
          <Title variant="h2">{data.title}</Title>
        </Desktop>
        {data.subTitle && <Subtitle variant="body1">{data.subTitle}</Subtitle>}
      </Heading>

      <ItemsContainer>
        {data.faqLists.map((item, index) => {
          const isOpen = expanded === index
          const isPrevToExpanded = expanded === index + 1
          return (
            <StyledAccordion
              key={index}
              className={isPrevToExpanded ? "prev-to-expanded" : undefined}
              square
              expanded={isOpen}
              onChange={handleChange(index)}
            >
              <StyledAccordionSummary
                expandIcon={isOpen ? <RemoveIcon /> : <AddIcon />}
                aria-controls={`faq-panel-${index}-content`}
                id={`faq-panel-${index}-header`}
              >
                <QuestionText className={isOpen ? "is-expanded" : undefined}>
                  {item.question}
                </QuestionText>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <AnswerText
                  variant="body2"
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(item.answer, ANSWER_SANITIZE_CONFIG),
                  }}
                />
              </StyledAccordionDetails>
            </StyledAccordion>
          )
        })}
      </ItemsContainer>
    </Section>
  )
}

export default FAQComp
